import { Eye, MoreVertical, Trash2, UserCheck, UserX } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@bookdental/ui";
import { Avatar } from "@/components/Avatar";
import { useConfirm } from "@/lib/confirm";

export type AcoesDoMembro = {
  member: any;
  canManage: boolean;
  suspendPending: boolean;
  reactivatePending: boolean;
  removePending: boolean;
  onOpenProfile: (membershipId: string) => void;
  onSuspend: (member: any) => void;
  onReactivate: (member: any) => void;
  onRemove: (member: any) => void;
};

/** Menu de ações do membro, usado pela linha da tabela (desktop) e pelo cartão (celular). Ficou
 *  aqui quando a lista ganhou as duas formas — o menu é idêntico nas duas, e as confirmações das
 *  ações destrutivas moram junto pra não serem esquecidas numa delas. */
export function TeamMemberActions({
  member: m,
  canManage,
  suspendPending,
  reactivatePending,
  removePending,
  onOpenProfile,
  onSuspend,
  onReactivate,
  onRemove,
}: AcoesDoMembro) {
  const confirm = useConfirm();
  const isOwner = m.role === "OWNER";
  const isSuspended = m.status === "SUSPENDED";
  const displayName = m.user.name || m.user.email;

  if (!canManage || isOwner) return null;

  const confirmDescription = (message: string) => (
    <div className="flex items-start gap-2 pt-0.5">
      <Avatar src={m.user.image} name={m.user.name} size="xs" className="mt-0.5 shrink-0" />
      <span>
        <strong className="text-zinc-800">{displayName}</strong> {message}
      </span>
    </div>
  );

  const handleSuspend = async () => {
    const ok = await confirm({
      title: "Suspender acesso?",
      description: confirmDescription(
        "perde acesso à organização imediatamente. Histórico e vínculos são preservados — pode reativar quando quiser.",
      ),
      confirmText: "Suspender",
      destructive: true,
    });
    if (ok) onSuspend(m);
  };

  const handleRemove = async () => {
    const ok = await confirm({
      title: "Remover da organização?",
      description: confirmDescription(
        "perde o vínculo com a organização. Essa ação não pode ser desfeita.",
      ),
      confirmText: "Remover",
      destructive: true,
    });
    if (ok) onRemove(m);
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          className="flex h-11 w-11 shrink-0 items-center justify-center rounded-md text-zinc-400 transition-colors hover:bg-zinc-100 hover:text-zinc-700 md:h-7 md:w-7 dark:hover:bg-muted dark:hover:text-foreground"
          aria-label="Mais ações"
        >
          <MoreVertical className="h-4 w-4" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="end"
        className="min-w-[190px] border-zinc-200 bg-white p-1 shadow-xl dark:border-border dark:bg-card"
      >
        <DropdownMenuItem
          onClick={() => onOpenProfile(m.id)}
          className="flex cursor-pointer items-center gap-2 rounded-md px-2.5 py-2 text-sm"
        >
          <Eye className="h-4 w-4 text-zinc-500 dark:text-muted-foreground" />
          Ver perfil
        </DropdownMenuItem>
        {isSuspended ? (
          <DropdownMenuItem
            onClick={() => onReactivate(m)}
            disabled={reactivatePending}
            className="flex cursor-pointer items-center gap-2 rounded-md px-2.5 py-2 text-sm"
          >
            <UserCheck className="h-4 w-4 text-emerald-600" />
            Reativar acesso
          </DropdownMenuItem>
        ) : (
          <DropdownMenuItem
            onClick={handleSuspend}
            disabled={suspendPending}
            className="flex cursor-pointer items-center gap-2 rounded-md px-2.5 py-2 text-sm"
          >
            <UserX className="h-4 w-4 text-amber-600" />
            Suspender acesso
          </DropdownMenuItem>
        )}
        <DropdownMenuItem
          onClick={handleRemove}
          disabled={removePending}
          className="flex cursor-pointer items-center gap-2 rounded-md px-2.5 py-2 text-sm text-red-600 dark:text-red-400"
        >
          <Trash2 className="h-4 w-4" />
          Remover da organização
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
